import { useEffect, useState } from "react";
import { api, errText, photoUrl } from "../../lib/api";
import { compressImage } from "../../lib/image";
import { useSlideRotation } from "../../lib/useSlideRotation";
import { SlidesList, SlideExtras, SlideLook, LOOK_KEYS, newSlide, isLiveSlide } from "./HeroSlides";
import { fontStyle } from "../../lib/googleFonts";
import { motion } from "framer-motion";
import { animProps, TypedTitle, heroBtnStyle } from "../../lib/heroAnim";
import { HeroBackdrop, shadowStyle } from "../../components/HeroBackdrop";
import { HeroPhonePreview } from "./HeroPhonePreview";
import { TaglinesEditor } from "./TaglinesEditor";
import { RotatingTagline } from "../../components/RotatingTagline";
import { Monitor, Smartphone, Play, Pause } from "lucide-react";
import { SaveAsTemplate } from "./HeroTemplates";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Home as HomeIcon, Sparkles, RotateCcw, ImagePlus, X, Plus, Trash2, GripVertical } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { Slider } from "../../components/ui/slider";

const DEFAULTS = {
  badge: "Canada's friendliest pet marketplace",
  title_line1: "Find your next",
  title_line2: "best friend.",
  subtitle: "Adopt, buy and sell pets, supplies and services from trusted, verified sellers across Canada.",
  stats_suffix: "active listings nationwide 🇨🇦",
  hero_image: "",
  hero_strip: "", hero_strip_mobile: "", hero_strips: [],
  badge_fr: "", title_line1_fr: "", title_line2_fr: "", subtitle_fr: "", stats_suffix_fr: "",
  hero_height: 0, hero_text_pos: "center", stats_mode: "text", stats_text: "Trusted pet listings from coast to coast 🇨🇦", stats_text_fr: "", tagline_speed: 4,
  taglines: [{ text: "Trusted pet listings from coast to coast 🇨🇦", text_fr: "Annonces d'animaux de confiance d'un océan à l'autre 🇨🇦" }, { text: "New pets added daily", text_fr: "De nouveaux animaux ajoutés chaque jour" }],
};
const DEFAULT_IMG = "https://images.unsplash.com/photo-1450778869180-41d0601e046e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export function HeroSettings({ part = "hero" }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [prevLang, setPrevLang] = useState("en");
  const [device, setDevice] = useState("desktop");
  const [sel, setSel] = useState(0);
  const [total, setTotal] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSlides = (fn) => setForm((f) => ({ ...f, slides: fn(f.slides) }));
  const slide = form?.slides?.[Math.min(sel, (form?.slides?.length || 1) - 1)];
  const setSlideField = (k, v) => { setPlaying(false); setSlides((sl) => sl.map((x, i) => (i === sel ? { ...x, [k]: v } : x))); };
  const live = (form?.slides || []).filter(isLiveSlide);
  const [playing, setPlaying] = useState(false);
  const [pIdx] = useSlideRotation(live, !playing);
  const [replay, setReplay] = useState(0);
  useEffect(() => { setPlaying(false); }, [sel]);

  useEffect(() => {
    const init = (d) => setForm({ ...DEFAULTS, ...d, slides: d.slides?.length ? d.slides : [newSlide({ badge: d.badge, title_line1: d.title_line1, title_line2: d.title_line2, subtitle: d.subtitle, id: "slide-1" })] });
    api.get("/site/hero").then((r) => init(r.data)).catch(() => init(DEFAULTS));
    api.get("/meta").then((r) => setTotal(r.data.total)).catch(() => {});
  }, []);

  const onImage = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("hero_image", data.path);
      toast.success("Background image uploaded");
    } catch (err) { toast.error(errText(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (!form.slides.some((x) => x.enabled)) { setSaving(false); return toast.error("Keep at least one slide switched on"); }
      const { data } = await api.put("/admin/site/hero", form);
      setForm({ ...DEFAULTS, ...data });
      toast.success(part === "strip" ? "Search hero images saved" : "Homepage hero updated");
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="h-40 flex items-center justify-center"><Loader2 className="animate-spin text-[#F5551E]" /></div>;

  const ps = (playing ? live[pIdx] : slide) || slide || form;
  const selIdx = Math.min(sel, (form.slides.length || 1) - 1);
  const pickFrom = (o, k) => (prevLang === "fr" ? (o[`${k}_fr`] || o[k]) : o[k]);
  const pick = (k) => pickFrom(["stats_suffix", "stats_text"].includes(k) ? form : ps, k);
  const hasFrench = !!(form.stats_suffix_fr || form.slides.some((x) => x.badge_fr || x.title_line1_fr || x.title_line2_fr || x.subtitle_fr));
  const bg = form.hero_image ? photoUrl(form.hero_image) : DEFAULT_IMG;

  if (part === "strip") return <div className="max-w-5xl grid" data-testid="hero-strip-settings"><HeroStripCard form={form} set={set} save={save} saving={saving} /></div>;

  return (
    <div className="max-w-5xl grid lg:grid-cols-2 gap-6" data-testid="hero-settings">
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <HomeIcon size={18} className="text-[#F5551E]" />
          <h3 className="font-display font-bold text-[#12234A]">Homepage hero</h3>
        </div>
        <p className="text-xs text-slate-500 -mt-2">Edit the headline, supporting text and background photo. The floating paw stays in place.</p>

        {/* Background image */}
        <div>
          <Label>Background photo</Label>
          <div className="mt-1 flex items-center gap-3">
            <div className="relative h-16 w-28 rounded-xl overflow-hidden border border-slate-200 bg-slate-100 shrink-0">
              <img src={bg} alt="" className="h-full w-full object-cover" />
              {form.hero_image && (
                <button type="button" onClick={() => set("hero_image", "")} data-testid="hero-image-remove" className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black"><X size={12} /></button>
              )}
            </div>
            <label data-testid="hero-image-upload" className="flex items-center gap-1.5 text-sm font-semibold text-[#F5551E] cursor-pointer hover:underline">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} {form.hero_image ? "Replace photo" : "Upload photo"}
              <input type="file" accept="image/*" onChange={onImage} className="hidden" />
            </label>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Leave empty to use the default photo. Wide landscape images (≥1600px) look best.</p>
        </div>

        <SlidesList slides={form.slides} setSlides={setSlides} sel={sel} setSel={setSel} />

        {slide && (
          <div className="rounded-xl border border-[#F5551E]/30 bg-[#FFF4F0]/40 p-3 space-y-3" data-testid="hero-slide-editor">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs font-bold uppercase tracking-wider text-[#F5551E]">Editing slide {Math.min(sel, form.slides.length - 1) + 1}</p>
              <SaveAsTemplate slide={slide} key={slide.id} />
            </div>
            <HeroFields form={slide} set={setSlideField} suffix="" />
            <SlideLook slide={slide} set={setSlideField} mainBg={bg} others={form.slides.length - 1} onReplay={() => setReplay((r) => r + 1)}
              onCopyAll={() => { const look = Object.fromEntries(LOOK_KEYS.map((k) => [k, slide[k]])); setSlides((sl) => sl.map((x) => ({ ...x, ...look }))); toast.success(`Look copied to ${form.slides.length - 1} other slide${form.slides.length === 2 ? "" : "s"} — save to keep it`); }} />
            <SlideExtras slide={slide} set={setSlideField} />
            <details className="rounded-xl border border-slate-200 bg-white p-3" data-testid="hero-french-section">
              <summary className="text-sm font-semibold text-[#12234A] cursor-pointer">French version <span className="text-slate-400 font-normal">(optional — shows an EN/FR toggle on the homepage)</span></summary>
              <div className="mt-3 space-y-3"><HeroFields form={slide} set={setSlideField} suffix="_fr" placeholder /></div>
            </details>
          </div>
        )}

        <TaglinesEditor lines={form.taglines || []} setLines={(v) => set("taglines", v)} speed={form.tagline_speed || 4} setSpeed={(v) => set("tagline_speed", v)} total={total} />

        <div className="rounded-xl border border-slate-200 p-3" data-testid="hero-height-control">
          <div className="flex items-center justify-between gap-3">
            <Label className="m-0">Banner height</Label>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span data-testid="hero-height-value">{form.hero_height ? `${form.hero_height}px` : "Default (auto)"}</span>
              <Switch checked={!!form.hero_height} onCheckedChange={(on) => set("hero_height", on ? 560 : 0)} data-testid="hero-height-custom" />
            </div>
          </div>
          {!!form.hero_height && (
            <Slider value={[form.hero_height]} min={320} max={900} step={10} onValueChange={([v]) => set("hero_height", v)} className="mt-4" data-testid="hero-height-slider" />
          )}
          <p className="text-[11px] text-slate-400 mt-2">{form.hero_height ? "Same height on every screen. If the text and search box need more room, the banner grows to fit so nothing gets cut off. Preview shown at half size." : "Turn on to set an exact height for the blue banner and search area."}</p>
        </div>

        <div className="rounded-xl border border-slate-200 p-3 flex items-center justify-between gap-3" data-testid="hero-text-pos-control">
          <div><Label className="m-0">Headline position</Label><p className="text-[11px] text-slate-400">Moves the headline side only; the search box stays centred.</p></div>
          <div className="flex rounded-xl bg-slate-100 p-1 shrink-0">
            {[["top", "Top"], ["center", "Centre"], ["bottom", "Bottom"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => set("hero_text_pos", v)} data-testid={`hero-text-pos-${v}`}
                className={`h-8 px-3 rounded-lg text-xs font-semibold transition-colors ${(form.hero_text_pos || "center") === v ? "bg-white text-[#F5551E] shadow-sm" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <Button onClick={save} disabled={saving} data-testid="hero-save-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save changes"}
          </Button>
          <Button variant="outline" onClick={() => { setSel(0); setForm({ ...DEFAULTS, hero_strips: form.hero_strips, hero_image: form.hero_image, slides: [newSlide({ ...DEFAULTS, id: "slide-1" })] }); }} data-testid="hero-reset-btn" className="rounded-xl gap-1.5"><RotateCcw size={15} /> Reset to default</Button>
        </div>
      </div>

      {/* Live preview */}
      <div className="rounded-2xl border border-slate-200 overflow-hidden h-fit" data-testid="hero-preview">
        <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white border-b border-slate-200">
          <div className="inline-flex rounded-xl bg-slate-100 p-1">
            {[["desktop", "Desktop", Monitor], ["phone", "Phone", Smartphone]].map(([v, l, Icon]) => (
              <button key={v} type="button" onClick={() => setDevice(v)} data-testid={`hero-preview-device-${v}`}
                className={`h-8 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${device === v ? "bg-white text-[#F5551E] shadow-sm" : "text-slate-500"}`}><Icon size={14} /> {l}</button>
            ))}
          </div>
          {hasFrench && device === "phone" && (
            <div className="flex rounded-full bg-slate-100 p-0.5">
              {["en", "fr"].map((l) => <button key={l} onClick={() => setPrevLang(l)} data-testid={`hero-phone-lang-${l}`} className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full ${prevLang === l ? "bg-[#F5551E] text-white" : "text-slate-500"}`}>{l.toUpperCase()}</button>)}
            </div>
          )}
        </div>
        {device === "phone" ? <HeroPhonePreview form={form} slide={ps} pick={pick} lang={prevLang} statsLine={<RotatingTagline lines={form.taglines} lang={prevLang} total={total} speed={form.tagline_speed} testid="hero-phone-tagline" />} mainBg={bg} /> : (
        <div className={`relative bg-[#12234A] p-6 overflow-hidden flex ${{ top: "items-start", bottom: "items-end" }[form.hero_text_pos] || "items-center"}`}
          style={{ minHeight: form.hero_height ? Math.round(form.hero_height / 2) : 300 }} data-testid="hero-preview-box">
          <HeroBackdrop src={ps.bg_image ? photoUrl(ps.bg_image) : bg} overlay={ps.overlay} zoom={ps.zoom} />
          <div className="relative w-full">
            {hasFrench && (
              <div className="absolute -top-1 right-0 flex rounded-full bg-white/10 p-0.5">
                {["en", "fr"].map((l) => (
                  <button key={l} onClick={() => setPrevLang(l)} data-testid={`hero-preview-lang-${l}`} className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full ${prevLang === l ? "bg-[#F5551E] text-white" : "text-white/70"}`}>{l.toUpperCase()}</button>
                ))}
              </div>
            )}
            <motion.div key={`${ps.id}-${pIdx}-${ps.text_anim || ""}-${replay}`} {...animProps(ps.text_anim)} transition={{ duration: 0.45 }} data-testid="hero-preview-slide">
            <span className="inline-flex items-center gap-2 bg-white/10 text-white text-[11px] font-semibold px-3 py-1.5 rounded-full" style={{ ...fontStyle(ps.text_font), ...(ps.badge_color ? { color: ps.badge_color } : {}), ...(ps.badge_bg ? { backgroundColor: ps.badge_bg } : {}) }}>
              <Sparkles size={13} className={ps.badge_bg ? "" : "text-[#F5551E]"} /> {pick("badge")}
            </span>
            <div className="mt-4 flex items-start gap-2">
              <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_critter-classifieds/artifacts/r4i8kh4r_paw-logo.png" alt="" className="h-9 w-9 shrink-0 mt-1 object-contain" />
              <h1 className="font-display text-3xl font-extrabold text-white leading-[1.05]" style={{ ...shadowStyle(ps.text_shadow), ...fontStyle(ps.title_font), fontSize: 30 * (ps.desktop_scale || 100) / 100 }} data-testid="hero-preview-h1">
                {ps.text_anim === "typewriter"
                  ? <TypedTitle line1={pick("title_line1")} line2={pick("title_line2")} run line1Props={{ style: ps.line1_color ? { color: ps.line1_color } : undefined }}
                      line2Props={{ className: "text-[#F5551E]", style: ps.line2_color ? { color: ps.line2_color } : undefined }} />
                  : <><span style={ps.line1_color ? { color: ps.line1_color } : undefined}>{pick("title_line1")}</span><br /><span className="text-[#F5551E]" style={ps.line2_color ? { color: ps.line2_color } : undefined}>{pick("title_line2")}</span></>}
              </h1>
            </div>
            <p className="mt-4 text-sm text-slate-200" style={{ ...shadowStyle(ps.text_shadow), ...fontStyle(ps.text_font), ...(ps.subtitle_color ? { color: ps.subtitle_color } : {}), fontSize: 14 * (ps.desktop_scale || 100) / 100 }} data-testid="hero-preview-subtitle">{pick("subtitle")}</p>
            {(ps.buttons || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="hero-preview-buttons">
                {ps.buttons.map((b, i) => <span key={i} className={`h-8 px-3 rounded-lg text-xs font-semibold flex items-center ${i === 0 ? `${ps.btn_bg ? "" : "bg-[#F5551E]"} text-white` : "border border-white/40 text-white"}`} style={heroBtnStyle(ps, i)} data-testid={`hero-preview-btn-${i}`}>{prevLang === "fr" ? (b.label_fr || b.label) : b.label}</span>)}
              </div>
            )}
            </motion.div>
            <RotatingTagline lines={form.taglines} lang={prevLang} total={total} speed={form.tagline_speed} className="mt-3 text-xs text-slate-300" testid="hero-preview-tagline" />
          </div>
        </div>
        )}
        <div className="flex items-center gap-2 px-3 py-2 bg-white border-t border-slate-200 flex-wrap" data-testid="hero-preview-picker">
          <span className="text-[11px] font-semibold text-slate-500">Slide</span>
          {form.slides.map((x, i) => (
            <button key={x.id || i} type="button" onClick={() => setSel(i)} data-testid={`hero-preview-pick-${i}`} title={x.enabled ? "" : "This slide is switched off"}
              className={`h-7 min-w-7 px-2 rounded-lg text-xs font-bold transition-colors ${!playing && i === selIdx ? "bg-[#F5551E] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"} ${x.enabled ? "" : "opacity-50 line-through"}`}>{i + 1}</button>
          ))}
          {live.length > 1 && (
            <button type="button" onClick={() => setPlaying((p) => !p)} data-testid="hero-preview-play"
              className={`ml-auto h-7 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${playing ? "bg-[#12234A] text-white" : "border border-slate-200 text-[#12234A] hover:border-[#F5551E]"}`}>
              {playing ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Play rotation</>}
            </button>
          )}
        </div>
        <p className="text-[11px] text-slate-400 px-4 py-2 bg-slate-50" data-testid="hero-preview-status">{playing ? `Playing the rotation · slide ${form.slides.indexOf(live[pIdx]) + 1} of ${form.slides.length}` : `Showing slide ${selIdx + 1} (the one you're editing)`} · the number updates automatically on the real homepage.</p>
      </div>
    </div>
  );
}

function HeroFields({ form, set, suffix, placeholder }) {
  const ph = placeholder ? { badge: "Badge (FR)", t1: "Ligne 1", t2: "Ligne 2", sub: "Sous-titre", stat: "Suffixe stats" } : {};
  return (
    <>
      <div>
        <Label>Badge</Label>
        <Input value={form[`badge${suffix}`]} onChange={(e) => set(`badge${suffix}`, e.target.value)} data-testid={`hero-input-badge${suffix}`} placeholder={ph.badge} className="mt-1 h-10 rounded-xl" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Headline line 1</Label>
          <Input value={form[`title_line1${suffix}`]} onChange={(e) => set(`title_line1${suffix}`, e.target.value)} data-testid={`hero-input-title1${suffix}`} placeholder={ph.t1} className="mt-1 h-10 rounded-xl" />
        </div>
        <div>
          <Label>Headline line 2 <span className="text-[#F5551E] font-normal">(orange)</span></Label>
          <Input value={form[`title_line2${suffix}`]} onChange={(e) => set(`title_line2${suffix}`, e.target.value)} data-testid={`hero-input-title2${suffix}`} placeholder={ph.t2} className="mt-1 h-10 rounded-xl" />
        </div>
      </div>
      <div>
        <Label>Subtitle</Label>
        <Textarea value={form[`subtitle${suffix}`]} onChange={(e) => set(`subtitle${suffix}`, e.target.value)} data-testid={`hero-input-subtitle${suffix}`} rows={2} placeholder={ph.sub} className="mt-1 rounded-xl" />
      </div>
    </>
  );
}

const DEFAULT_STRIP = "/catndogmacaw.png";
const MAX_STRIPS = 10;

function StripUpload({ value, onChange, label, testid }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file, { keepPng: true }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-semibold text-slate-500 mb-1">{label}</p>
      <div className="rounded-lg bg-[#12234A] px-2 pt-2 h-16 flex items-end justify-center overflow-hidden">
        {value ? <img src={photoUrl(value)} alt="" className="max-h-full w-4/5 object-contain object-bottom" data-testid={`${testid}-preview`} /> : <span className="text-[10px] text-white/50 pb-5">None</span>}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <label data-testid={`${testid}-upload`} className="text-xs font-semibold text-[#F5551E] cursor-pointer hover:underline flex items-center gap-1">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} {value ? "Replace" : "Upload"}
          <input type="file" accept="image/*" onChange={onFile} className="hidden" />
        </label>
        {value && label.startsWith("Mobile") && <button type="button" onClick={() => onChange("")} data-testid={`${testid}-clear`} className="text-xs text-slate-400 hover:text-red-600">Remove</button>}
      </div>
    </div>
  );
}

// Rotating search hero images: one is picked at random on each page load (never the same twice in a row).
function HeroStripCard({ form, set, save, saving }) {
  const strips = form.hero_strips || [];
  const [drag, setDrag] = useState(null);
  const setStrips = (next) => set("hero_strips", next);
  const update = (i, patch) => setStrips(strips.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const add = () => setStrips([...strips, { id: `strip-${Math.random().toString(36).slice(2, 10)}`, enabled: true, desktop: "", mobile: "" }]);
  const drop = (to) => {
    if (drag === null || drag === to) return setDrag(null);
    const a = [...strips]; const [m] = a.splice(drag, 1); a.splice(to, 0, m); setStrips(a); setDrag(null);
  };
  const onCount = strips.filter((x) => x.enabled && x.desktop).length;
  return (
    <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="hero-strip-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ImagePlus size={18} className="text-[#F5551E]" />
          <h3 className="font-display font-bold text-[#12234A]">Search hero images</h3>
          <span className="text-xs text-slate-400" data-testid="hero-strip-count">({onCount} on{onCount ? "" : " · showing default"})</span>
        </div>
        <div className="flex items-center gap-3">
          {strips.length > 0 && <button type="button" onClick={() => setStrips([])} data-testid="hero-strip-reset" className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#12234A]"><RotateCcw size={14} /> Reset to default</button>}
          <button type="button" onClick={add} disabled={strips.length >= MAX_STRIPS} data-testid="hero-strip-add" className="text-sm font-semibold text-[#F5551E] flex items-center gap-1 hover:underline disabled:opacity-40"><Plus size={15} /> Add image</button>
        </div>
      </div>
      <p className="text-xs text-slate-500 -mt-2">The animal strip resting on top of the homepage search box (80% of its width). With several images on, visitors see a random one on each page load, never the same one twice in a row. Transparent PNGs (about 5:1, e.g. 1000×200px) look best. Up to {MAX_STRIPS}.</p>
      {!strips.length && (
        <div className="rounded-xl bg-[#12234A] px-4 pt-4 overflow-hidden max-w-md" data-testid="hero-strip-default">
          <img src={DEFAULT_STRIP} alt="" className="block w-4/5 mx-auto -mb-1" />
          <div className="h-5 bg-white rounded-t-xl" />
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {strips.map((st, i) => (
          <div key={st.id} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(i)} onDragEnd={() => setDrag(null)}
            data-testid={`hero-strip-${i}`} className={`rounded-xl border p-3 flex gap-2 items-start ${drag === i ? "opacity-50" : ""} ${st.enabled ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
            <div className="flex flex-col items-center gap-2 pt-5">
              <GripVertical size={16} className="text-slate-400 cursor-grab" />
              <span className="h-5 w-5 rounded-full bg-[#12234A] text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
            </div>
            <StripUpload label="Desktop" value={st.desktop} onChange={(v) => update(i, { desktop: v })} testid={`hero-strip-${i}-desktop`} />
            <StripUpload label="Mobile (optional)" value={st.mobile} onChange={(v) => update(i, { mobile: v })} testid={`hero-strip-${i}-mobile`} />
            <div className="flex flex-col items-center gap-2 pt-5">
              <Switch checked={st.enabled} onCheckedChange={(v) => update(i, { enabled: v })} data-testid={`hero-strip-${i}-toggle`} />
              <button type="button" onClick={() => setStrips(strips.filter((_, k) => k !== i))} data-testid={`hero-strip-${i}-delete`} title="Delete" className="h-7 w-7 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      {strips.some((x) => !x.desktop) && <p className="text-xs text-amber-700" data-testid="hero-strip-missing">Images without a desktop picture are skipped when you save.</p>}
      <Button onClick={save} disabled={saving} data-testid="hero-strip-save-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">
        {saving ? <Loader2 size={16} className="animate-spin" /> : "Save hero images"}
      </Button>
    </div>
  );
}
